import type { AreaDetail, AreaFeedItem, AreaFeedResponse } from "./apiTypes";
import { formatManilaClockRange, formatManilaWindow, formatWindowDuration, manilaDayLabel } from "./countdown";
import { computeGlance, glanceAccuracyNote, type Glance } from "./glance";
import { renderReportControl } from "./reports";
import { STATUS_METADATA } from "./statusStyles";
import {
  buildTimelineSlots,
  formatRelativeSpan,
  pairPanelId,
  pairedSlotSummary,
  renderTimeline,
  sourceLinkLabel,
} from "./timeline";

function appendText(parent: HTMLElement, tagName: keyof HTMLElementTagNameMap, text: string, className?: string): HTMLElement {
  const element = document.createElement(tagName) as HTMLElement;
  element.textContent = text;
  if (className) {
    element.className = className;
  }
  parent.appendChild(element);
  return element;
}

function appendExternalLink(parent: HTMLElement, url: string, label = "Original post"): HTMLAnchorElement {
  const link = document.createElement("a");
  link.href = url;
  link.textContent = label;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  parent.appendChild(link);
  return link;
}

export function renderAreaPanelShell(container: HTMLElement, key: string): void {
  container.textContent = "";
  const section = document.createElement("section");
  section.className = "area-panel-card";
  section.dataset.areaPanelShell = key;
  section.setAttribute("aria-busy", "true");
  appendText(section, "p", "Loading area details...", "muted");
  appendText(section, "h2", key);
  container.appendChild(section);
}

function countdownText(glance: Glance, now: Date): string | null {
  if (!glance.countdown) {
    return null;
  }
  const target = new Date(glance.countdown.target);
  const span = formatRelativeSpan(now, target);
  if (span === "now") {
    return glance.countdown.kind === "ends_in" ? "Ending now" : "Starting now";
  }
  const future = target.getTime() > now.getTime();
  if (glance.countdown.kind === "ends_in") {
    return future ? `Ends ${span}` : `Ended ${span}`;
  }
  return future ? `Starts ${span}` : `Started ${span}`;
}

/** Raw statuses that describe something that already happened; they render in the Previously block (R-621). */
const PAST_STATUSES = new Set<string>(["restored", "cancelled"]);

function appendStatusPill(parent: HTMLElement, area: AreaDetail): void {
  const status = document.createElement("span");
  status.className = "status-pill";
  status.dataset.status = area.status.value;
  status.textContent = area.status.label || STATUS_METADATA[area.status.value].label;
  parent.appendChild(status);
  if (area.status.stale) {
    const stale = document.createElement("span");
    stale.className = "stale-tag";
    stale.textContent = "data stale";
    parent.appendChild(document.createTextNode(" "));
    parent.appendChild(stale);
  }
}

function decorateHeroPairChip(button: HTMLButtonElement, kind: "sources_differ" | "revised"): void {
  if (kind === "sources_differ") {
    const glyph = document.createElement("span");
    glyph.className = "pair-glyph";
    glyph.setAttribute("aria-hidden", "true");
    glyph.textContent = "⇄";
    button.appendChild(glyph);
    button.appendChild(document.createTextNode("Sources differ"));
    return;
  }
  button.textContent = "Revised";
}

function appendHeroPairChip(
  hero: HTMLElement,
  area: AreaDetail,
  feed: AreaFeedResponse,
  now: Date,
): void {
  const slots = buildTimelineSlots(feed.items, area, now);
  const summary = pairedSlotSummary(slots, now);
  if (!summary) {
    return;
  }
  const panelId = pairPanelId(summary.slot.start, slots.indexOf(summary.slot));
  const button = document.createElement("button");
  button.type = "button";
  button.className = "status-pill pair-chip pair-chip-hero";
  button.dataset.pairTarget = panelId;
  decorateHeroPairChip(button, summary.kind);
  button.addEventListener("click", () => {
    const root = button.closest(".area-panel-card") ?? document;
    const panel = root.querySelector<HTMLElement>(`#${panelId}`);
    const row = panel?.closest("li");
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    if (panel) {
      panel.hidden = false;
    }
    const slotChip = row?.querySelector<HTMLButtonElement>("button.pair-chip");
    slotChip?.setAttribute("aria-expanded", "true");
  });

  let said = hero.querySelector<HTMLElement>(".glance-said");
  if (!said) {
    said = document.createElement("p");
    said.className = "glance-said";
    hero.appendChild(said);
  }
  said.appendChild(button);
}

/**
 * R-617/R-621/R-627: answer-first hero for the current or next window (tier label, live countdown,
 * window, expected duration, the raw status pill, the accuracy note and the report control). Closure statuses (Restored today /
 * Cancelled) render on the timeline's Now row instead, next to the power-on state.
 */
function renderGlanceHero(
  section: HTMLElement,
  area: AreaDetail,
  glance: Glance,
  now: Date,
  feed: AreaFeedResponse | null = null,
): void {
  const hero = document.createElement("div");
  hero.className = "glance-hero";
  hero.dataset.tier = glance.tier;
  hero.dataset.glanceHero = area.key;

  const kicker = appendText(hero, "p", glance.tier === "off" || glance.tier === "clear" ? "Now" : "Next", "glance-kicker");
  kicker.dataset.tier = glance.tier;

  const label = appendText(hero, "p", glance.label, "glance-label");
  label.dataset.tier = glance.tier;

  const countdown = countdownText(glance, now);
  if (glance.countdown && countdown) {
    const line = appendText(hero, "p", countdown, "glance-countdown rel-time");
    line.dataset.relTarget = glance.countdown.target;
    line.dataset.relKind = glance.countdown.kind;
  } else if (glance.tier === "clear") {
    appendText(hero, "p", "Power on", "glance-countdown");
  }

  if (glance.window) {
    const start = new Date(glance.window.start);
    appendText(
      hero,
      "p",
      `${manilaDayLabel(start, now)} · ${formatManilaClockRange(glance.window)} PHT`,
      "glance-when",
    );
    const duration = formatWindowDuration(glance.window);
    if (duration) {
      appendText(hero, "p", `${duration} expected`, "glance-duration");
    }
  }

  // R-639: the map is only as accurate as the announcement or calendar that drove it.
  appendText(hero, "p", glanceAccuracyNote(area.status.source), "glance-note");

  const pastStatus = PAST_STATUSES.has(area.status.value);
  if (!pastStatus) {
    const said = document.createElement("p");
    said.className = "glance-said";
    appendStatusPill(said, area);
    hero.appendChild(said);
  }

  if (feed) {
    appendHeroPairChip(hero, area, feed, now);
  }

  // R-640: ask for the ground truth; nothing is shown back yet (R-642).
  renderReportControl(hero, area, glance, now);

  const existing = section.querySelector<HTMLElement>("[data-glance-hero]");
  if (existing) {
    existing.replaceWith(hero);
  } else {
    section.appendChild(hero);
  }
}

export function renderAreaPanel(container: HTMLElement, area: AreaDetail, now = new Date()): void {
  container.textContent = "";
  const glance = computeGlance(
    {
      status: area.status.value,
      stale: area.status.stale,
      lastKnownStatus: area.status.lastKnownStatus,
      activeWindow: area.status.activeWindow,
      nextWindow: area.status.nextWindow,
    },
    now,
  );

  const section = document.createElement("section");
  section.className = "area-panel-card";
  section.dataset.areaKey = area.key;
  section.dataset.tier = glance.tier;

  const head = document.createElement("div");
  head.className = "area-head";
  appendText(head, "h2", area.displayName || area.key);
  appendText(head, "p", `Area key: ${area.key}`, "area-key");
  if (area.provisional) {
    const notice = appendText(
      head,
      "p",
      "Estimated area. This outline was generated automatically from VECO's map image and can be off by a block or more. Check VECO's original map before relying on it. ",
      "notice estimated-notice",
    );
    if (area.mapUrl) {
      appendExternalLink(notice, area.mapUrl, "Open VECO's map");
    }
  }
  section.appendChild(head);

  renderGlanceHero(section, area, glance, now);

  if (!area.mapped) {
    const notice = appendText(
      section,
      "p",
      "No polygon yet — this area is named in advisories but cannot be drawn on the map.",
      "notice",
    );
    if (area.mapUrl) {
      notice.appendChild(document.createTextNode(" "));
      appendExternalLink(notice, area.mapUrl, "Open VECO's map");
    }
  }

  const timeline = document.createElement("section");
  timeline.className = "area-timeline";
  timeline.dataset.areaTimeline = area.key;
  appendText(timeline, "h3", "Timeline");
  appendText(timeline, "p", "Loading outage windows...", "muted");
  section.appendChild(timeline);

  const source = document.createElement("p");
  source.className = "source-links";
  if (area.status.sourceUrl) {
    appendExternalLink(source, area.status.sourceUrl, "Original status post");
  } else {
    source.textContent = "No original status post link available.";
  }
  section.appendChild(source);

  const feed = document.createElement("details");
  feed.className = "area-feed";
  feed.dataset.areaFeed = area.key;
  const summary = document.createElement("summary");
  summary.className = "area-feed-toggle";
  appendText(summary, "h3", "Area feed");
  feed.appendChild(summary);
  const body = document.createElement("div");
  body.className = "area-feed-body";
  body.dataset.areaFeedBody = "";
  appendText(body, "p", "Loading updates...", "muted");
  feed.appendChild(body);
  section.appendChild(feed);

  container.appendChild(section);
}

/** Fills the timeline once the feed has loaded; closure statuses land on the Now row (R-627). */
export function renderAreaTimeline(container: HTMLElement, area: AreaDetail, feed: AreaFeedResponse, now = new Date()): void {
  const timeline = container.querySelector<HTMLElement>("[data-area-timeline]");
  if (!timeline) {
    return;
  }
  const slots = buildTimelineSlots(feed.items, area, now);
  renderTimeline(timeline, slots, now);

  const section = timeline.closest<HTMLElement>("section.area-panel-card");
  if (section) {
    const glance = computeGlance(
      {
        status: area.status.value,
        stale: area.status.stale,
        lastKnownStatus: area.status.lastKnownStatus,
        activeWindow: area.status.activeWindow,
        nextWindow: area.status.nextWindow,
      },
      now,
    );
    renderGlanceHero(section, area, glance, now, feed);
  }

  if (!PAST_STATUSES.has(area.status.value)) {
    return;
  }
  const nowSaid = timeline.querySelector<HTMLElement>("[data-timeline-now-said]");
  const hero = container.querySelector<HTMLElement>("[data-glance-hero]");
  const host = nowSaid ?? hero;
  if (!host || host.querySelector(".status-pill")) {
    return;
  }
  const said = document.createElement(nowSaid ? "span" : "p");
  said.className = "glance-said";
  appendStatusPill(said, area);
  host.appendChild(said);
}

function feedGroupKey(item: AreaFeedItem): string {
  return `${item.kind}|${item.statusEffect}|${item.superseded ? "superseded" : "current"}|${item.text.trim().replace(/\s+/g, " ")}`;
}

/** R-630: how a replaced or vanished event is explained in the feed list. */
export function feedItemNote(item: AreaFeedItem): string | null {
  if (item.postMissing || item.supersededReason === "post_missing") {
    return "Post no longer visible on Facebook";
  }
  if (item.supersededReason === "master_revision" || item.supersededReason === "seeded_master_replaced") {
    return "Replaced by a newer schedule";
  }
  if (item.supersededReason === "edited_post") {
    return "Replaced by an edited post";
  }
  if (item.supersededReason === "newer_source") {
    return item.source === "portal_sheet"
      ? "Replaced by a newer Facebook post"
      : "Replaced by a newer report on VECO's calendar";
  }
  if (item.supersededReason === "sheet_revision") {
    return "Replaced by a revised schedule on VECO's calendar";
  }
  return item.superseded ? "Superseded" : null;
}

function appendFeedNote(row: HTMLElement, item: AreaFeedItem): void {
  const note = feedItemNote(item);
  if (!note) {
    return;
  }
  row.classList.add("is-superseded");
  appendText(row, "p", note, "feed-note");
}

function latestWindowStart(items: AreaFeedItem[]): string | null {
  let latest: string | null = null;
  for (const item of items) {
    const start = item.window?.start;
    if (start && (latest === null || start > latest)) {
      latest = start;
    }
  }
  return latest;
}

function sortGroupItems(items: AreaFeedItem[]): AreaFeedItem[] {
  return [...items].sort((a, b) => {
    const aStart = a.window?.start;
    const bStart = b.window?.start;
    if (aStart && bStart) {
      return aStart < bStart ? 1 : aStart > bStart ? -1 : 0;
    }
    if (aStart) {
      return -1;
    }
    if (bStart) {
      return 1;
    }
    return 0;
  });
}

export function groupFeedItems(items: AreaFeedItem[]): AreaFeedItem[][] {
  const groups = new Map<string, AreaFeedItem[]>();
  const order: string[] = [];
  for (const item of items) {
    const key = feedGroupKey(item);
    const existing = groups.get(key);
    if (existing) {
      existing.push(item);
    } else {
      groups.set(key, [item]);
      order.push(key);
    }
  }

  const windowed: { items: AreaFeedItem[]; start: string }[] = [];
  const windowless: AreaFeedItem[][] = [];
  for (const key of order) {
    const group = groups.get(key) ?? [];
    const start = latestWindowStart(group);
    if (start) {
      windowed.push({ items: sortGroupItems(group), start });
    } else {
      windowless.push(group);
    }
  }
  windowed.sort((a, b) => (a.start < b.start ? 1 : a.start > b.start ? -1 : 0));
  return [...windowed.map((group) => group.items), ...windowless];
}

function appendOriginalPost(parent: HTMLElement, item: AreaFeedItem): void {
  if (!item.postUrl) {
    return;
  }
  const linkRow = document.createElement("p");
  appendExternalLink(linkRow, item.postUrl, sourceLinkLabel(item.source));
  parent.appendChild(linkRow);
}

function normalisedText(text: string): string {
  return text.trim().replace(/\s+/g, " ");
}

function renderSingleFeedItem(row: HTMLElement, item: AreaFeedItem): void {
  appendText(row, "p", normalisedText(item.text));
  appendFeedNote(row, item);
  appendText(row, "p", `${item.kind} - ${item.statusEffect}`, "feed-meta");
  if (item.window) {
    appendText(row, "p", formatManilaWindow(item.window), "feed-window");
  }
  appendOriginalPost(row, item);
}

function renderFeedGroup(list: HTMLOListElement, items: AreaFeedItem[]): void {
  const row = document.createElement("li");
  const latest = items[0];
  if (!latest) {
    return;
  }
  if (items.length === 1) {
    renderSingleFeedItem(row, latest);
    list.appendChild(row);
    return;
  }

  const text = document.createElement("p");
  const strong = document.createElement("strong");
  strong.textContent = normalisedText(latest.text);
  text.appendChild(strong);
  row.appendChild(text);
  appendFeedNote(row, latest);
  appendText(row, "p", `${latest.kind} - ${latest.statusEffect}`, "feed-meta");
  appendText(row, "p", `${items.length} windows · latest ${formatManilaWindow(latest.window)}`, "feed-window");
  appendOriginalPost(row, latest);

  const details = document.createElement("details");
  details.className = "feed-history";
  const summary = document.createElement("summary");
  summary.textContent = `Show all ${items.length} updates`;
  details.appendChild(summary);
  const history = document.createElement("ol");
  for (const item of items) {
    const historyRow = document.createElement("li");
    if (item.window) {
      appendText(historyRow, "p", formatManilaWindow(item.window), "feed-window");
    }
    appendOriginalPost(historyRow, item);
    history.appendChild(historyRow);
  }
  details.appendChild(history);
  row.appendChild(details);
  list.appendChild(row);
}

export function renderAreaFeed(container: HTMLElement, response: AreaFeedResponse): void {
  const feed = container.querySelector<HTMLElement>("[data-area-feed]");
  if (!feed) {
    return;
  }

  let body = feed.querySelector<HTMLElement>("[data-area-feed-body]");
  if (!body) {
    body = document.createElement("div");
    body.className = "area-feed-body";
    body.dataset.areaFeedBody = "";
    feed.appendChild(body);
  }
  body.textContent = "";
  if (response.items.length === 0) {
    appendText(body, "p", "No feed updates yet.", "muted");
    return;
  }

  const list = document.createElement("ol");
  list.className = "feed-list";
  for (const group of groupFeedItems(response.items)) {
    renderFeedGroup(list, group);
  }
  body.appendChild(list);
}
